// FILENAME: /frontend/src/components/Settings.jsx
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardHeader,
  CardContent,
  CircularProgress,
  Grid,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useApi from '../hooks/useApi';
import { logger } from '../utils/logger';
const LazyApiPlayground = React.lazy(() => import('./ApiPlayground'));
const LazyDbExplorer = React.lazy(() => import('./DbExplorer'));
const LazyCronJobsPanel = React.lazy(() => import('./CronJobsPanel'));
const LazyBulkUploadTracker = React.lazy(() => import('./BulkUploadTracker'));
const LazyApplicationMetrics = React.lazy(() => import('./ApplicationMetrics'));
const LazySnapshotHistory = React.lazy(() => import('./SnapshotHistory'));
const LazyDbMigrationTests = React.lazy(() => import('./DbMigrationTests'));
const LazyRealtimeFeed = React.lazy(() => import('./RealtimeFeed'));
const LazyCacheDiagnostics = React.lazy(() => import('./CacheDiagnostics'));
const LazyPWAStatus = React.lazy(() => import('./PWAStatus'));
const LazyApiKeyManager = React.lazy(() => import('./ApiKeyManager'));
const LazyAppSheetManager = React.lazy(() => import('./AppSheetManager'));
const LazyEmailAlertsManager = React.lazy(() => import('./EmailAlertsManager'));
const LazyReportsOpsPanel = React.lazy(() => import('./reports/ReportsOpsPanel'));
import { useThemeMode } from '../hooks/useThemeMode.jsx';
import ApiPresenceSettingsCard from './ApiPresenceSettingsCard.jsx';
import WebsiteOwnershipConfigCard from './WebsiteOwnershipConfigCard.jsx';

const credentialTitles = {
  other: 'All Other Programs Credentials (GET)',
  trade: 'Trade Program Credentials (GET)',
  vmt_prod: 'All Other Programs VMT Credentials (POST)',
  vmt_nonprod: 'Trade Program VMT Credentials (POST)',
};

const TAB_CONFIG = [
  { value: 'credentials', label: 'Credentials' },
  { value: 'email-alerts', label: 'Email Alerts' },
  { value: 'api-keys', label: 'API Keys' },
  { value: 'appsheet', label: 'AppSheet' },
  { value: 'pwa', label: 'PWA Status' },
  { value: 'api', label: 'API Inspector' },
  { value: 'bulk-uploads', label: 'Bulk Uploads' },
  { value: 'db', label: 'DB Explorer' },
  { value: 'jobs', label: 'Schedulers' },
  { value: 'metrics', label: 'App Metrics' },
  { value: 'cache', label: 'Cache Diagnostics' },
  { value: 'reports-ops', label: 'Reports Ops' },
  { value: 'wip', label: 'WIP' },
  { value: 'snapshots', label: 'Snapshots' },
  { value: 'db-tests', label: 'DB Migration Tests' },
  { value: 'realtime', label: 'Realtime' },
];

const CredentialForm = ({ type, credentials, onSave, onTest }) => {
  const [formState, setFormState] = useState({
    clientId: '',
    clientSecret: '',
    scope: '',
    tokenUrl: '',
  });

  useEffect(() => {
    if (credentials) {
      setFormState({
        clientId: credentials.clientId || '',
        clientSecret: '',
        scope: credentials.scope || '',
        tokenUrl: credentials.tokenUrl || '',
      });
    }
  }, [credentials]);

  const handleChange = (event) => {
    setFormState({ ...formState, [event.target.name]: event.target.value });
  };

  const handleSave = () => {
    onSave({ type, ...formState });
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {credentialTitles[type] || 'API Credentials'}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Client ID"
              name="clientId"
              value={formState.clientId}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Client Secret"
              name="clientSecret"
              type="password"
              placeholder="Enter new secret to update"
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Scope" name="scope" value={formState.scope} onChange={handleChange} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Token URL" name="tokenUrl" value={formState.tokenUrl} onChange={handleChange} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
          <Button variant="outlined" onClick={() => onTest(type)}>
            Test Connection
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

const CredentialsTabContent = ({ isLoading, credentials, onSave, onTest }) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const order = ['other', 'trade', 'vmt_prod', 'vmt_nonprod'];

  return (
    <Grid container spacing={3}>
      {order.map((type) => (
        <Grid key={type} item xs={12} md={6}>
          <CredentialForm type={type} credentials={credentials[type]} onSave={onSave} onTest={onTest} />
        </Grid>
      ))}
    </Grid>
  );
};

const ApiInspectorTabContent = ({ nullSubmission, onNullSubmission }) => (
  <>
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Null Submission
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sends an empty payload to GM to indicate no product selection updates for the day.
        </Typography>
        <Button variant="outlined" onClick={onNullSubmission} disabled={nullSubmission.isPending}>
          {nullSubmission.isPending ? 'Sending…' : 'Send Null Record'}
        </Button>
      </CardContent>
    </Card>
    <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>
      <LazyApiPlayground />
    </React.Suspense>
  </>
);

const Settings = () => {
  const navigate = useNavigate();
  const {
    useGetCredentials,
    useSaveCredentials,
    useTestCredentials,
    useVmtNullSubmission,
    useGetApiPresenceSettings,
    useSaveApiPresenceSettings,
    useGetWebsiteOwnershipConfig,
    useSaveWebsiteOwnershipConfig,
  } = useApi();
  const { data: credentialsData, isLoading: isLoadingCreds } = useGetCredentials();
  const saveMutation = useSaveCredentials();
  const testMutation = useTestCredentials();
  const nullSubmission = useVmtNullSubmission();
  const { data: apiPresenceSettings, isLoading: isLoadingApiPresenceSettings } = useGetApiPresenceSettings();
  const saveApiPresenceSettings = useSaveApiPresenceSettings();
  const { data: websiteOwnershipConfig, isLoading: isLoadingWebsiteOwnershipConfig } = useGetWebsiteOwnershipConfig();
  const saveWebsiteOwnershipConfig = useSaveWebsiteOwnershipConfig();
  const { preference, setPreference } = useThemeMode();

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [activeTab, setActiveTab] = useState('credentials');

  const handleDownloadLogs = () => {
    const payload = {
      capturedAt: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      logs: logger.getHistory(),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `gmer-debug-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setSnackbar({ open: true, message: 'Debug logs downloaded.', severity: 'success' });
  };

  const handleSave = (credentials) => {
    saveMutation.mutate(credentials, {
      onSuccess: (data) => setSnackbar({ open: true, message: data.message, severity: 'success' }),
      onError: (error) =>
        setSnackbar({
          open: true,
          message: error.response?.data?.message || 'Failed to save',
          severity: 'error',
        }),
    });
  };

  const handleTest = (type) => {
    testMutation.mutate(type, {
      onSuccess: (data) => setSnackbar({ open: true, message: data.message, severity: 'success' }),
      onError: (error) =>
        setSnackbar({
          open: true,
          message: error.response?.data?.message || 'Test failed',
          severity: 'error',
        }),
    });
  };

  const handleNullSubmission = () => {
    nullSubmission.mutate(undefined, {
      onSuccess: () =>
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Null submission sent successfully.',
        }),
      onError: (error) =>
        setSnackbar({
          open: true,
          severity: 'error',
          message: error?.response?.data?.message || 'Null submission failed.',
        }),
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const credentialMap = {
    other: credentialsData?.find((c) => c.type === 'other'),
    trade: credentialsData?.find((c) => c.type === 'trade'),
    vmt_prod: credentialsData?.find((c) => c.type === 'vmt_prod'),
    vmt_nonprod: credentialsData?.find((c) => c.type === 'vmt_nonprod'),
  };

  return (
    <Box>
      <ApiPresenceSettingsCard
        settings={apiPresenceSettings}
        isLoading={isLoadingApiPresenceSettings}
        saveMutation={saveApiPresenceSettings}
        onMessage={({ message, severity }) => setSnackbar({ open: true, message, severity })}
      />
      <Card sx={{ mb: 2 }}>
        <CardHeader title="Appearance" subheader="Theme preference is saved on this device." />
        <CardContent>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Theme</InputLabel>
            <Select
              value={preference}
              label="Theme"
              onChange={(e) => setPreference(e.target.value)}
            >
              <MenuItem value="system">System</MenuItem>
              <MenuItem value="dark">Dark</MenuItem>
              <MenuItem value="light">Light</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>
      <Card sx={{ mb: 2 }}>
        <CardHeader title="Diagnostics" subheader="Export recent console + network logs for troubleshooting." />
        <CardContent>
          <Button variant="outlined" onClick={handleDownloadLogs}>
            Download Debug Logs
          </Button>
        </CardContent>
      </Card>
      <Tabs
        value={activeTab}
        onChange={(_event, value) => {
          if (value === 'wip') {
            navigate('/__wip');
            return;
          }
          setActiveTab(value);
        }}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        {TAB_CONFIG.map((tab) => (
          <Tab key={tab.value} label={tab.label} value={tab.value} />
        ))}
      </Tabs>

      <Box sx={{ mt: 3 }}>
        {activeTab === 'credentials' && (
          <>
            <WebsiteOwnershipConfigCard
              config={websiteOwnershipConfig}
              isLoading={isLoadingWebsiteOwnershipConfig}
              saveMutation={saveWebsiteOwnershipConfig}
              testPending={testMutation.isPending}
              onTest={() => handleTest('website_ownership_auditor')}
              onMessage={({ message, severity }) => setSnackbar({ open: true, message, severity })}
            />
            <CredentialsTabContent
              isLoading={isLoadingCreds}
              credentials={credentialMap}
              onSave={handleSave}
              onTest={handleTest}
            />
          </>
        )}
        {activeTab === 'email-alerts' && (
          <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>
            <LazyEmailAlertsManager />
          </React.Suspense>
        )}
        {activeTab === 'api-keys' && (
          <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>
            <LazyApiKeyManager />
          </React.Suspense>
        )}
        {activeTab === 'appsheet' && (
          <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>
            <LazyAppSheetManager />
          </React.Suspense>
        )}
        {activeTab === 'pwa' && (
          <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>
            <LazyPWAStatus showDetails={true} />
          </React.Suspense>
        )}
        {activeTab === 'api' && (
          <ApiInspectorTabContent nullSubmission={nullSubmission} onNullSubmission={handleNullSubmission} />
        )}
        {activeTab === 'bulk-uploads' && (
          <Box sx={{ mt: 2 }}>
            <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>
              <LazyBulkUploadTracker />
            </React.Suspense>
          </Box>
        )}
        {activeTab === 'db' && (
          <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>
            <LazyDbExplorer />
          </React.Suspense>
        )}
        {activeTab === 'jobs' && (
          <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>
            <LazyCronJobsPanel />
          </React.Suspense>
        )}
        {activeTab === 'metrics' && (
          <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>
            <LazyApplicationMetrics />
          </React.Suspense>
        )}
        {activeTab === 'cache' && (
          <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>
            <LazyCacheDiagnostics />
          </React.Suspense>
        )}
        {activeTab === 'reports-ops' && (
          <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>
            <LazyReportsOpsPanel />
          </React.Suspense>
        )}
        {activeTab === 'snapshots' && (
          <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>
            <LazySnapshotHistory />
          </React.Suspense>
        )}
        {activeTab === 'db-tests' && (
          <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>
            <LazyDbMigrationTests />
          </React.Suspense>
        )}
        {activeTab === 'realtime' && (
          <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>
            <LazyRealtimeFeed />
          </React.Suspense>
        )}
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
